import React, { useState } from "react";
import useSelector from "store/useSelector";
import "./ManageSWRefDocs.scoped.scss";
import RefDoc from "./RefDoc";
import { useDispatch } from "react-redux";
import { addNewReferenceDoc, addNewReferenceDocTable, pasteComponentDraftRefDoc, updateRefDocSortOrders } from "store/manageSW/manageSWActions";
import sortIcon from "media/icons/dls/sort.svg";
import ReorderModal, { ISortableItem } from "components/common/reorderModal/ReorderModal";
import FlowLayout from "components/layout/FlowLayout";
import { IReferenceDoc, ITableComponentCell, SWTypes } from "interfaces/sw/SWInterfaces";
import useLocalStorageItemStore from "../../../../utilities/localStorageClipboard";
import { getImageName } from "../../../../utilities/fileUtilities";
import { showErrorToast } from "../../../../store/toast/toastActions";

interface IManageSWRefDocsProps {
  isDisabled: boolean,
}

const ManageSWRefDocs: React.FC<IManageSWRefDocsProps> = ({ isDisabled }) => {
  const {
    manageSW: {
      SW: {
        guid,
        referenceDocs,
        type: swType,
      }
    },
  } = useSelector(store => store);
  const dispatch = useDispatch();
    const [isReorderVisible, setIsReorderVisible] = useState(false);
    const [copiedComponentState, setCopiedComponent] = useLocalStorageItemStore("authoring.copied-component");

    const getImageFilenames = (imageUrls: string[]) => {
        let imageFileNames: string[] = [];
        for (let i = 0; i < imageUrls.length; i++) {
            let fileName: string | null = getImageName(imageUrls[i]);
            if (fileName != null) {
                imageFileNames.push(fileName);
            }
        }
        return imageFileNames;
    }
    const getTableImageFilenames = (cells: ITableComponentCell[]) => {
        const fileNames: string[] = [];
        for (let i = 0; i < cells.length; i++) {
            if (cells[i].imageSourceFileName !== undefined) {
                fileNames.push(cells[i].imageSourceFileName!);
            }
            else if (cells[i].imageSourceFileName === undefined &&
                cells[i].imageFileName !== undefined) {
                fileNames.push(cells[i].imageFileName!);
            }
        }
        return fileNames;
    }

    const pasteComponentFromClipboardInRefDoc = () => {
        try {
            const invalidDataMessage = "Please copy a valid Component before proceeding.";
            if (!copiedComponentState) {
                dispatch(showErrorToast(invalidDataMessage));
                return;
            }
            let copyCompObj = JSON.parse(copiedComponentState);
            let imageFilenames: string[] = [];

            if (copyCompObj.refDoc) {
                // find out if in table has images the get all the images
                if (copyCompObj.refDocType === 'Table' || copyCompObj.refDocType === 'Link') {
                    if (copyCompObj.refDocType === 'Table') {
                        imageFilenames = getImageFilenames(getTableImageFilenames(copyCompObj.refDoc.tableData.cells));
                    }
                    dispatch(pasteComponentDraftRefDoc({
                        swGuid: guid,
                        refDoc: copyCompObj.refDoc,
                        sourceSWGuid: copyCompObj.swGuid,
                        sourceSWVersion: copyCompObj.swVersion,
                        filenames: imageFilenames
                    }));
                }
                else {
                    dispatch(showErrorToast("Unable to paste. " + (copyCompObj.refDocType + " Component is not supported in Ref Docs.")));
                }
            }
            else {
                //copied from task
                let convertedRefDocObj: IReferenceDoc;
                if (copyCompObj.component.type === 'Link') {
                    convertedRefDocObj = {
                        id: copyCompObj.component.id,
                        guid: copyCompObj.component.guid,
                        refDocData: copyCompObj.component.url,
                        label: copyCompObj.component.label,
                        type: copyCompObj.component.type,
                        lcl: false
                    }
                    dispatch(pasteComponentDraftRefDoc({
                        swGuid: guid,
                        refDoc: convertedRefDocObj,
                        sourceSWGuid: copyCompObj.swGuid,
                        sourceSWVersion: copyCompObj.swVersion,
                        filenames: imageFilenames
                    }));
                }
                else if (copyCompObj.component.type === 'Table') {
                    convertedRefDocObj = {
                        id: copyCompObj.component.id,
                        guid: copyCompObj.component.guid,
                        refDocData: copyCompObj.component,
                        label: copyCompObj.component.label,
                        type: copyCompObj.component.type,
                        lcl: false,
                        tableData: copyCompObj.component
                    }
                    imageFilenames = getImageFilenames(getTableImageFilenames(copyCompObj.component.cells));
                    dispatch(pasteComponentDraftRefDoc({
                        swGuid: guid,
                        refDoc: convertedRefDocObj,
                        sourceSWGuid: copyCompObj.swGuid,
                        sourceSWVersion: copyCompObj.swVersion,
                        filenames: imageFilenames
                    }));
                }
                else {
                    dispatch(showErrorToast("Unable to paste. " + (copyCompObj.component.type + " Component is not supported in Ref Docs.")));
                }
            }
        } catch (err: any) {
            setCopiedComponent("");
            dispatch(showErrorToast(err));
        }
    }

  return (
    <FlowLayout
      footer={!isDisabled
        ? (
          <div className="manage-ref-docs">
            <div className="buttons">
              {!isDisabled
                && referenceDocs.length > 0 &&
                <button
                  className="secondary-button"
                  title={"Reorder"}
                  onClick={() => setIsReorderVisible(true)}
                >
                  <img
                    alt="Sort"
                    title="Sort"
                    className="icon-small sort-icon"
                    src={sortIcon}
                  />
                  Reorder
                </button>
              }
              {!isDisabled &&
                <button
                  className="primary-button"
                  title={"Add Ref Doc"}
                  onClick={() => dispatch(addNewReferenceDoc({
                    type: "File",
                  }))}
                >
                  + Add Ref Doc
                </button>
              }
              {!isDisabled &&
                <button
                  className="primary-button"
                  title={"Add Table"}
                  onClick={() => dispatch(addNewReferenceDocTable())}
                >
                  + Add Table
                </button>
              }
              {!isDisabled &&
                <button
                  className="primary-button"
                  title={"Add Link"}
                  onClick={() => dispatch(addNewReferenceDoc({
                    type: "Link",
                  }))}
                >
                  + Add Link
                </button>
              }
              {!isDisabled &&
                  <button
                    className="secondary-button"
                    title="Paste Component"
                    onClick={pasteComponentFromClipboardInRefDoc}                    disabled={!copiedComponentState}                   >
                    Paste Component
                  </button>

              }
            </div>
            {isReorderVisible &&
              <ReorderModal
                title="Reorder Ref Docs"
                items={referenceDocs
                  .slice()
                  .sort((a, b) => {
                    const aSort = a.sortOrder || a.id;
                    const bSort = b.sortOrder || b.id;

                    if (!aSort
                      || !bSort) {
                      return 0;
                    }

                    return aSort < bSort ? -1 : 1;
                  }).map(item => ({
                    label: item.label || item.refDocData,
                    sortOrder: item.sortOrder || item.id || 0,
                    guid: item.guid,
                  }))
                }
                onCancel={() => setIsReorderVisible(false)}
                onSave={(sortData: ISortableItem[]) => {
                  dispatch(updateRefDocSortOrders(sortData.map((x, ix) => ({
                    guid: x.guid,
                    newIndex: ix,
                  }))));
                  setIsReorderVisible(false);
                }}
              />
            }
          </div>
        ) : undefined
      }
    >
      <div
        className="holder"
      >
        <div className="ref-docs">
          {referenceDocs.map(refDoc =>
            <RefDoc
              key={refDoc.guid}
              refDoc={refDoc}
              disabled={isDisabled || (swType === SWTypes.LCL && !refDoc.lcl)}
            />
          )}
        </div>
      </div>
    </FlowLayout>
  );
}

export default ManageSWRefDocs;